<script context="module" lang="ts">
  import { Http, HttpPromise } from "@shared/svelte/Modules/ApiService";
  import type { Distributor } from "@shared/types/Distributor";
  import type {
    Inventory,
    ProductLine,
    Product,
    MiniProductLine,
    CartProduct,
  } from "@shared/types/ProductLine";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import Flash from "@shared/services/flashv2.js";
  import { CustomPriceEntity } from "@shared/services/event_tracking/entities";
  const { trackEvent } = EventTracking;

  export const getDistributors = async (productLineId: number): Promise<Distributor[]> => {
    return HttpPromise({
      method: "GET",
      url: "/api/retailer/product_lines/" + productLineId + "/distributors/",
    });
  };

  export const saveDistributor = function (
    productLine: ProductLine,
    selectedDistributor: Distributor,
    cb: any,
    errCb: any,
  ) {
    Http(
      {
        method: "POST",
        url: "/api/retailer/product_lines/" + productLine.id + "/distributor/",
        data: {
          distributor: {
            id: selectedDistributor.id,
          },
        },
      },
      cb,
      errCb,
    );
  };

  const finishPriceChange = (res, product: Product | CartProduct) => {
    if (!res) {
      return;
    }
    if (res.unit_price) {
      product.unit_price = res.unit_price;
      trackEvent(
        "Custom Unit Price Created",
        new CustomPriceEntity({
          location: "Retailer - Product Detail",
          price: res.unit_price,
        }),
      );
    }
    if (res.case_price) {
      product.case_price = res.case_price;
      trackEvent(
        "Custom Case Price Created",
        new CustomPriceEntity({
          location: "Retailer - Product Detail",
          price: res.case_price,
        }),
      );
    }
  };

  export const saveCustomPrice = function (
    productLine: ProductLine | MiniProductLine,
    product: Product | CartProduct,
    data: { custom_price: any },
    cb: () => void,
    errCb: () => void,
  ) {
    if (product.has_custom_price) {
      Http(
        {
          method: "PUT",
          url: "/api/retailer/products/" + product.id,
          data: data,
        },
        function (res) {
          finishPriceChange(res, product);
          cb();
        },
        function error(res) {
          Flash.now({
            message: "There was an error adding custom pricing for product " + product.id,
            type: "error",
          });
          errCb();
        },
      );
    } else {
      Http(
        {
          method: "POST",
          url: "/api/retailer/custom_prices",
          data: {
            custom_price: {
              product_id: product.id,
              unit_price: data.custom_price.unit_price || 0,
              case_price: data.custom_price.case_price || 0,
              distributor_id: productLine.distributor.id,
            },
          },
        },
        function (res) {
          product.has_custom_price = true;
          product.custom_price_id = res.id;
          finishPriceChange(res, product);
          cb();
        },
        function (res) {
          Flash.now({
            message: "There was an error adding custom pricing for product " + product.id,
            type: "error",
          });
          errCb();
        },
      );
    }
  };

  export const deleteCustomPriceApi = function (product: Partial<Product>, cb: () => void) {
    Http(
      {
        method: "DELETE",
        url: "/api/retailer/custom_prices/" + product.custom_price_id,
        data: {},
      },
      function (res) {
        if (!res) {
          Flash.now({
            message: "There was an error deleting this custom pricing information.",
            type: "error",
          });
          return;
        }
        product.unit_price = res.unit_price;
        product.case_price = res.case_price;
        product.has_custom_price = false;
        Flash.now({ message: "Deleted custom pricing!", type: "success" });
        cb();
      },
      function () {
        Flash.now({
          message: "There was an error deleting this custom pricing information.",
          type: "error",
        });
      },
    );
  };

  export const getInventory = function (
    inventoryId: number,
    successCb: (inventory: Inventory) => void,
    errorCb: any,
  ) {
    Http(
      {
        method: "GET",
        url: `/api/retailer/inventory/${inventoryId}`,
      },
      successCb,
      () => {
        if (errorCb) {
          errorCb();
        } else {
          Flash.now({
            message: "There was an error loading this product.",
            type: "error",
          });
        }
      },
    );
  };
</script>
